// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface Sport {
  id: string;
  name: string;
  gender: string | null;
  created_at: string;
}

interface SportFormData {
  name: string;
  gender: string;
}

const SportsManagement: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSport, setEditingSport] = useState<Sport | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<SportFormData>({
    name: '',
    gender: ''
  });

  useEffect(() => {
    console.log('🔍 Sports Admin Loaded - SportsManagement.tsx');
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('sports')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching sports:', error);
        toast.error('Failed to load sports');
        return;
      }

      setSports(data || []);
    } catch (error) {
      console.error('Error fetching sports:', error);
      toast.error('Failed to load sports');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSport = async () => {
    if (!formData.name.trim()) {
      toast.error('Sport name is required');
      return;
    }

    if (!formData.gender) {
      toast.error('Gender selection is required');
      return;
    }

    console.log('🔍 Attempting to add sport with values:', {
      name: formData.name.trim(),
      gender: formData.gender
    });

    setIsSubmitting(true);
    try {
      const insertData = {
        name: formData.name.trim(),
        gender: formData.gender,
      };

      console.log('🔍 Insert data prepared:', insertData);

      const { data, error } = await supabase
        .from('sports')
        .insert([insertData])
        .select()
        .single();

      console.log('🔍 Supabase insert result:', { data, error });

      if (error) {
        console.error('❌ Error adding sport:', error);
        console.warn('Insert failed — Gender submitted:', formData.gender);
        if (error.message.includes('row-level security') || error.code === 'PGRST301') {
          toast.error('Failed to add sport: Row-Level Security may be preventing this operation. Please check admin permissions.');
        } else {
          toast.error(`Failed to add sport: ${error.message}`);
        }
        return;
      }

      console.log('✅ Sport added successfully');
      // Refresh the sports list
      await fetchSports();
      setFormData({ name: '', gender: '' });
      setIsAddModalOpen(false);
      toast.success('Sport added successfully');
    } catch (error) {
      console.error('❌ Unexpected error adding sport:', error);
      toast.error('Failed to add sport');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSport = async () => {
    if (!editingSport || !formData.name.trim()) {
      toast.error('Sport name is required');
      return;
    }

    if (!formData.gender) {
      toast.error('Gender selection is required');
      return;
    }

    console.log('🔍 Attempting to update sport:', editingSport.id, 'with values:', {
      name: formData.name.trim(),
      gender: formData.gender
    });

    setIsSubmitting(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        gender: formData.gender,
      };

      const { data, error } = await supabase
        .from('sports')
        .update(updateData)
        .eq('id', editingSport.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating sport:', error);
        console.warn('Update failed — Gender submitted:', formData.gender);
        toast.error('Failed to update sport');
        return;
      }

      console.log('✅ Sport updated successfully');
      setSports(sports.map(sport => 
        sport.id === editingSport.id ? data : sport
      ));
      setFormData({ name: '', gender: '' });
      setEditingSport(null);
      setIsEditModalOpen(false);
      toast.success('Sport updated successfully');
    } catch (error) {
      console.error('❌ Unexpected error updating sport:', error);
      toast.error('Failed to update sport');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSport = async (sportId: string) => {
    console.log('🔍 Attempting to delete sport:', sportId);
    try {
      const { error } = await supabase
        .from('sports')
        .delete()
        .eq('id', sportId);

      if (error) {
        console.error('❌ Error deleting sport:', error);
        toast.error('Failed to delete sport');
        return;
      }

      console.log('✅ Sport deleted successfully');
      setSports(sports.filter(sport => sport.id !== sportId));
      toast.success('Sport deleted successfully');
    } catch (error) {
      console.error('❌ Unexpected error deleting sport:', error);
      toast.error('Failed to delete sport');
    }
  };

  const openEditModal = (sport: Sport) => {
    setEditingSport(sport);
    setFormData({
      name: sport.name,
      gender: sport.gender || ''
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', gender: '' });
    setEditingSport(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading sports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Sports Management</h2>
          <p className="text-gray-600">Manage all sports available in the platform</p>
        </div>
        
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Sport
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Sport</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Sport Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter sport name"
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender (Required)</Label>
                <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Men">Men</SelectItem>
                    <SelectItem value="Women">Women</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddSport} 
                  disabled={isSubmitting || !formData.name.trim() || !formData.gender}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add Sport
                </Button>
                <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Sports ({sports.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {sports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No sports found. Add your first sport to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sport Name</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sports.map((sport) => (
                  <TableRow key={sport.id}>
                    <TableCell className="font-medium">{sport.name}</TableCell>
                    <TableCell>{sport.gender || 'Any'}</TableCell>
                    <TableCell>{new Date(sport.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(sport)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Sport</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{sport.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteSport(sport.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sport</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Sport Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter sport name"
              />
            </div>
            <div>
              <Label htmlFor="edit-gender">Gender (Required)</Label>
              <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Men">Men</SelectItem>
                  <SelectItem value="Women">Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleEditSport} 
                disabled={isSubmitting || !formData.name.trim() || !formData.gender}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update Sport
              </Button>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SportsManagement;